import { useMemo } from "react";
import {
  EdmMedicalPerson,
  useEdmGetAllStaffWithGenreAndWorkingHoursQuery,
} from "lib/graphql/megaSchema";
import { isDoctor, isMidwife, isNurse } from "lib/tools/auth";
import { StaffGenreFilter } from "views/StaffSchedule/tools";

type Props = {
  searchInputValue: string;
  selectedStaffGenre: StaffGenreFilter;
};

export const useStaffSettings = ({
  searchInputValue,
  selectedStaffGenre,
}: Props) => {
  const nameFilter = (e: EdmMedicalPerson) =>
    e.firstname
      ?.trim()
      .toLowerCase()
      .includes(searchInputValue.toLowerCase()) ||
    e.lastname?.trim().toLowerCase().includes(searchInputValue.toLowerCase());

  const { data, loading, error } =
    useEdmGetAllStaffWithGenreAndWorkingHoursQuery({
      variables: {
        input: { genre: ["DOCTOR", "NURSE"] },
      },
    });

  const allStaff = data?.edmGetAllStaffWithGenreAndWorkingHours
    .map((list) => list.medicalPerson)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  const filteredStaff = useMemo(() => {
    if (selectedStaffGenre === "DOCTOR") {
      return allStaff?.filter(isDoctor).filter(nameFilter);
    }

    if (selectedStaffGenre === "NURSE") {
      return allStaff?.filter(isNurse).filter(nameFilter);
    }

    if (selectedStaffGenre === "MIDWIFE") {
      return allStaff?.filter(isMidwife).filter(nameFilter);
    }

    return allStaff?.filter(nameFilter);
  }, [selectedStaffGenre, searchInputValue, allStaff]);

  if (!data || error) {
    return {
      filteredStaff: null,
      loading,
    };
  }

  return {
    filteredStaff,
    loading,
  };
};
